import { FC, useEffect, useState } from "react";
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withTiming,
} from "react-native-reanimated";

import {
  Box,
  HStack,
  Progress,
  ProgressFilledTrack,
  Skeleton,
  Text,
  VStack,
} from "@/components/ui";
import { useLocalization } from "@/hooks";

export interface LanguageProgressItem {
  completed: number;
  label: string;
  language: string;
  total: number;
}

interface TranslationsProgressProps {
  languages: LanguageProgressItem[];
}

// Representative flag per language code
const LANGUAGE_FLAGS: Record<string, string> = {
  bs: "🇧🇦",
  bg: "🇧🇬",
  hr: "🇭🇷",
  cs: "🇨🇿",
  da: "🇩🇰",
  nl: "🇳🇱",
  en: "🇬🇧",
  "en-gb": "🇬🇧",
  "en-us": "🇺🇸",
  et: "🇪🇪",
  fi: "🇫🇮",
  fr: "🇫🇷",
  ka: "🇬🇪",
  de: "🇩🇪",
  el: "🇬🇷",
  hi: "🇮🇳",
  hu: "🇭🇺",
  hy: "🇦🇲",
  id: "🇮🇩",
  is: "🇮🇸",
  it: "🇮🇹",
  ja: "🇯🇵",
  kk: "🇰🇿",
  ko: "🇰🇷",
  ky: "🇰🇬",
  lv: "🇱🇻",
  lt: "🇱🇹",
  ms: "🇲🇾",
  no: "🇳🇴",
  pl: "🇵🇱",
  pt: "🇵🇹",
  "pt-br": "🇧🇷",
  "pt-pt": "🇵🇹",
  ro: "🇷🇴",
  ru: "🇷🇺",
  sr: "🇷🇸",
  sk: "🇸🇰",
  sl: "🇸🇮",
  es: "🇪🇸",
  "es-419": "🌎",
  sv: "🇸🇪",
  tr: "🇹🇷",
  uk: "🇺🇦",
  yue: "🇭🇰",
  zh: "🇨🇳",
  "zh-hans": "🇨🇳",
  "zh-hant": "🇹🇼",
};

// "Preparing content..." in each language
const PREPARING_CONTENT: Record<string, string> = {
  bs: "Priprema sadržaja...",
  bg: "Подготовка на съдържание...",
  hr: "Priprema sadržaja...",
  cs: "Příprava obsahu...",
  da: "Forbereder indhold...",
  nl: "Inhoud voorbereiden...",
  en: "Preparing content...",
  "en-gb": "Preparing content...",
  "en-us": "Preparing content...",
  et: "Sisu ettevalmistamine...",
  fi: "Valmistellaan sisältöä...",
  fr: "Préparation du contenu...",
  ka: "კონტენტის მომზადება...",
  de: "Inhalt wird vorbereitet...",
  el: "Προετοιμασία περιεχομένου...",
  hi: "सामग्री तैयार हो रही है...",
  hu: "Tartalom előkészítése...",
  hy: "Բովանդակության նախապատրաստում...",
  id: "Mempersiapkan konten...",
  is: "Undirbúningur efnis...",
  it: "Preparazione del contenuto...",
  ja: "コンテンツを準備中...",
  kk: "Мазмұнды дайындау...",
  ko: "콘텐츠 준비 중...",
  ky: "Мазмунду даярдоо...",
  lv: "Satura sagatavošana...",
  lt: "Turinio rengimas...",
  ms: "Menyediakan kandungan...",
  no: "Forbereder innhold...",
  pl: "Przygotowywanie treści...",
  pt: "Preparando conteúdo...",
  "pt-br": "Preparando conteúdo...",
  "pt-pt": "A preparar conteúdo...",
  ro: "Pregătire conținut...",
  ru: "Подготовка контента...",
  sr: "Припрема садржаја...",
  sk: "Príprava obsahu...",
  sl: "Priprava vsebine...",
  es: "Preparando contenido...",
  "es-419": "Preparando contenido...",
  sv: "Förbereder innehåll...",
  tr: "İçerik hazırlanıyor...",
  uk: "Підготовка вмісту...",
  yue: "準備內容中...",
  zh: "准备内容中...",
  "zh-hans": "准备内容中...",
  "zh-hant": "準備內容中...",
};

const TranslationsSkeleton: FC = () => (
  <Box className="flex-row flex-wrap">
    {Array.from({ length: 9 }).map((_, i) => (
      <Box key={i} className="w-1/3 p-1.5">
        <VStack className="gap-2 bg-background-50 rounded-lg p-4">
          <HStack className="items-center gap-2">
            <Skeleton className="w-5 h-4 rounded" />
            <Skeleton className="h-2.5 flex-1 rounded" />
          </HStack>
          <Skeleton className="h-2 w-3/4 rounded" />
          <Skeleton className="h-1.5 w-full rounded-full" />
        </VStack>
      </Box>
    ))}
  </Box>
);

type LanguageCardProps = {
  index: number;
  item: LanguageProgressItem;
};

const LanguageCard: FC<LanguageCardProps> = ({ index, item }) => {
  const { i18n } = useLocalization();
  const { language, label, completed, total } = item;
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  const delay = index * 80;
  const flag = LANGUAGE_FLAGS[language] ?? "🌐";
  const phrase =
    PREPARING_CONTENT[language] ?? i18n.t("studies.preparingContent");

  const [progressValue, setProgressValue] = useState(0);
  const opacity = useSharedValue(0);
  const translateY = useSharedValue(8);

  useEffect(() => {
    opacity.value = withDelay(delay, withTiming(1, { duration: 300 }));
    translateY.value = withDelay(
      delay,
      withTiming(0, { duration: 300, easing: Easing.out(Easing.ease) })
    );
    const t = setTimeout(() => {
      requestAnimationFrame(() =>
        requestAnimationFrame(() => setProgressValue(pct))
      );
    }, delay + 300);
    return () => clearTimeout(t);
  }, [delay, opacity, pct, translateY]);

  const cardStyle = useAnimatedStyle(() => ({
    opacity: opacity.value,
    transform: [{ translateY: translateY.value }],
  }));

  return (
    <Animated.View style={[cardStyle, { width: "100%" }]}>
      <VStack className="gap-2 bg-background-50 rounded-lg p-4">
        <HStack className="items-center gap-2">
          <Text className="text-lg">{flag}</Text>
          <Text
            className="text-[11px] font-medium text-typography-700"
            numberOfLines={1}
          >
            {label}
          </Text>
        </HStack>
        <Text
          className="text-[10px] text-typography-400 italic"
          numberOfLines={1}
        >
          {phrase}
        </Text>
        <HStack className="items-center gap-2">
          <Progress
            className="flex-1 bg-background-200"
            size="xs"
            value={progressValue}
          >
            <ProgressFilledTrack className="bg-tertiary-500 [transition:width_0.6s_ease-out]" />
          </Progress>
          <Text className="text-[10px] font-semibold text-typography-500 w-7 text-right">
            {i18n.numberToPercentage(pct, { precision: 0 })}
          </Text>
        </HStack>
      </VStack>
    </Animated.View>
  );
};

const TranslationsProgress: FC<TranslationsProgressProps> = ({ languages }) => {
  if (languages.length === 0) return <TranslationsSkeleton />;

  return (
    <Box className="flex-row flex-wrap">
      {languages.slice(0, 15).map((item, idx) => (
        <Box key={item.language} className="w-1/3 p-1.5">
          <LanguageCard index={idx} item={item} />
        </Box>
      ))}
    </Box>
  );
};

export default TranslationsProgress;
